import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AmorgAdminToken, AmorgAdminUserListing, OrganizationNote, OrganizationNoteFlag, OrganizationNoteFlags } from '../../../amorg-common';
import { createOrganizationNote, deleteOrganizationNote, getActiveUser, getOrganizationNotes, getUsers, updateOrganizationNote } from '../../../api/adminClientApi';
import { BaseMessagePanel } from '../../../designSystem/compositeComponents/BaseMessagePanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { SafeFlagSelector } from '../../../designSystem/compositeComponents/SafeFlagSelector';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../designSystem/Message';
import { PageSection } from '../../../designSystem/PageSection';
import { AdminSiteTab } from '../_components/AdminSiteTabs';
import { AmorgyPageWrapper } from '../../_pageWrappers/AmorgyPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';

export function AmorgAdminOrganizationNotes (): JSX.Element
{
    const [searchParams] = useSearchParams();
    const { organizationId } = useParams<{ organizationId: string }>();
    const siteId = searchParams.get('siteId') || undefined;
    const navigate = useNavigate();
    const [notes, setNotes] = React.useState<OrganizationNote[]>([]);
    const [users, setUsers] = React.useState<AmorgAdminUserListing[]>([]);
    const [flagFilter, setFlagFilter] = React.useState<OrganizationNoteFlag[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    async function loadNotes ()
    {
        if(!organizationId)
        {
            setError('Mangler organizationId i URL');
            setLoading(false);
            return;
        }

        try
        {
            setNotes(await getOrganizationNotes(organizationId, () => navigate('/amorgy/login')));
            setActiveUser(await getActiveUser(() => navigate('/amorgy/login')));
            setError('');
        }
        catch
        {
            setError('Noter kunne ikke indlæses');
        }
        finally
        {
            setLoading(false);
        }
    }

    React.useEffect(() =>
    {
        async function getData ()
        {
            const users = await getUsers(() => navigate('/amorgy/login'));
            setUsers(users || []);
        }
        getData();
    }, [organizationId]);

    React.useEffect(() =>
    {
        loadNotes();
    }, [organizationId]);

    async function handleCreateNote (content:string, tags?:string[])
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke oprettes' };

        const created = await createOrganizationNote(
            organizationId,
            {
                flags: tags === undefined ? [] : tags as OrganizationNoteFlag[],
                note: content,
            },
            () => navigate('/amorgy/login')
        );
        if(!created)
        {
            setError('Noten kunne ikke oprettes');
            return { errorMessage: 'Noten kunne ikke oprettes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    async function handleUpdateNote (id:string, content:string, tags?:string[])
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke gemmes' };

        const noteObject = notes.find(n => n.id === id);
        if(!noteObject)
            return { errorMessage: 'Noten kunne ikke gemmes' };

        const updated = await updateOrganizationNote(
            organizationId,
            { ...noteObject, flags: tags === undefined ? noteObject.flags : tags as OrganizationNoteFlag[], note:content, updated: new Date() },
            () => navigate('/amorgy/login')
        );
        if(!updated)
        {
            setError('Noten kunne ikke gemmes');
            return { errorMessage: 'Noten kunne ikke gemmes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    async function handleDeleteNote (noteId: string)
    {
        if(!organizationId)
            return { errorMessage: 'Noten kunne ikke slettes' };

        const deleted = await deleteOrganizationNote(organizationId, noteId, () => navigate('/amorgy/login'));
        if(!deleted)
        {
            setError('Noten kunne ikke slettes');
            return { errorMessage: 'Noten kunne ikke slettes' };
        }
        await loadNotes();
        return { errorMessage: undefined };
    }

    const filteredNotes = React.useMemo(() => {
        if(flagFilter.length === 0)
            return notes;
        else
            return notes.filter(note => note.flags.some(flag => flagFilter.includes(flag)));
    }, [notes, flagFilter]);

    return (
        <AmorgyPageWrapper selectedTab='organizations' 
            subMenuContent={
                <AdminSiteTab selectedSiteTab="notes" orgId={organizationId} siteId={siteId} />
            }>

            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout narrow>
                    <Flex flexDirection="column" gap={24}>
                        {error && <MessageCard variant="error">{error}</MessageCard>}
                        {/* <Flex justifyContent="flex-end">
                            <Button variant="text" text="Tilføj note" icon="actions_plus" onClick={handleCreateNote} />
                        </Flex> */}

                        <SafeFlagSelector
                            colorMode='colorIndex'
                            allowedFlags={OrganizationNoteFlags.map(flag => flag)}
                            selectedFlags={flagFilter}
                            onChange={(newFlags) => setFlagFilter(newFlags as OrganizationNoteFlag[])} />

                        <PageSection header={{ title: 'Noter', defaultCollapsed: false }} contentStyleVariant='card'>

                            <BaseMessagePanel
                                texts={{
                                    addButton: 'Tilføj note',
                                    deletedConfirmation: 'Noten er slettet',
                                    deleteModalConfirmation: 'Er du sikker på, at du vil slette noten?',
                                    deleteModalHeading: 'Slet note',
                                    editModalHeader: 'Rediger note',
                                    editModalLabel: 'Rediger note',
                                    noComments: 'Ingen noter oprettet',
                                }}
                                messages={filteredNotes.map(note => {
                                    const user = users.find(u => u.userId === note.createdByUserId);
                                    const createdBy = user
                                        ? { userId: user.userId, displayName: user.email }
                                        : { userId: note.createdByUserId, displayName: 'Ukendt bruger' };

                                    const canChange = activeUser && (activeUser.superAdmin || activeUser.userId === note.createdByUserId) && !note.deleted;

                                    const onEdit = canChange
                                        ? (_id: string, content: string, tags?: string[]) => handleUpdateNote(note.id, content, tags)
                                        : undefined;

                                    const onDelete = canChange
                                        ? () => handleDeleteNote(note.id)
                                        : undefined;

                                    return {
                                        id: note.id,
                                        content: note.deleted ? '[Slettet]' : note.note,
                                        tags: note.flags,
                                        createdBy,
                                        created: note.created,
                                        updated: note.updated,
                                        deleted: note.deleted,
                                        onEdit,
                                        onDelete,
                                    }
                                })}
                                availableTags={OrganizationNoteFlags.map(flag => flag)}
                                maxHeightPx={99999999999}
                                createDisabled={false}
                                messageValidator={content => {
                                    if(content.length < 1)
                                        return { errorMessage: 'Noten må ikke være tom' };

                                    return { errorMessage: undefined };
                                }}
                                onCreate={handleCreateNote}
                            />

                        </PageSection>
                    </Flex>
                </SingleColumnLayout>
        </AmorgyPageWrapper>
    );
}